import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashValues from 'lodash/fp/values';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  PARTNER_TYPES,
} from 'common/partnerConstants';

import {
  SR_API_ACTION_ADMIN_CREATE_PARTNER_SALE,
} from 'api/actionTypes';

import {
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import withApiState from 'client/helpers/withApiState';
import branch from 'client/core/branch';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrFormikField, {
  SR_FIELD_THEME_ADMIN,
} from 'client/components/SrFormikField';

import postWithState from 'client/actions/postWithState';
import MarketActions from 'client/actions/Market';

function getFormFields({ marketList }) {
  return [
    {
      id: 'partnerName',
      type: 'short_text',
      title: 'Partner name',
      validations: ['required'],
    },
    {
      id: 'partnerPhone',
      type: 'phone',
      title: 'Partner phone',
      validations: ['required', 'phone'],
    },
    {
      id: 'partnerType',
      type: 'select',
      title: 'Partner Type',
      properties: {
        choices: PARTNER_TYPES.map((partnerType) => ({
          label: partnerType,
          value: partnerType,
        })),
      },
      validations: ['required'],
    },
    {
      id: 'leasesPastEighteenMonths',
      type: 'number',
      title: 'Leases past eighteen months',
      validations: ['required'],
    },
    {
      id: 'salesPastEighteenMonths',
      type: 'number',
      title: 'Sales past eighteen months',
      validations: ['required'],
    },
    {
      id: 'marketId',
      type: 'select',
      title: 'Market',
      properties: {
        choices: marketList.map((market) => ({
          label: market.displayName,
          value: market.id,
        })),
      },
      validations: ['required'],
    },
    {
      id: 'upcomingPropertyAddress',
      type: 'short_text',
      title: 'Upcoming property address',
      label: '(optional)',
    },
    {
      id: 'upcomingPropertyMlsNumber',
      type: 'short_text',
      title: 'Upcoming property MLS number',
      label: '(optional)',
    },
    {
      id: 'partnerLogoUrl',
      type: 'file_single',
      title: 'Partner logo URL',
      label: '(optional)',
      properties: {
        fileAccept: '*',
        filePath: '/partner-uploads/logos/{uuid}',
      },
    },
  ];
}

@withApiState({
  adminRegisterPartnerSaleApiState: {
    action: SR_API_ACTION_ADMIN_CREATE_PARTNER_SALE,
  },
})
@branch({
  marketsById: ['markets', 'byId'],
})
class SrCreatePartnerSaleModal extends Component {
  static propTypes = {
    marketsById: PropTypes.object.isRequired,
    adminRegisterPartnerSaleApiState: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
  }

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(MarketActions.getList);
  }

  _onSubmitForm = (formValues) => {
    const { onSubmit, dispatch } = this.props;
    const payload = setNullEmptyValues(formValues);

    payload.marketId = payload.marketId
      ? parseInt(payload.marketId, 10)
      : null;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_CREATE_PARTNER_SALE,
      payload: payload,
    }).then(onSubmit);
  }

  render() {
    const { adminRegisterPartnerSaleApiState, marketsById, onClose } = this.props;
    const marketList = lodashValues(marketsById);
    const formFields = getFormFields({ marketList });

    return (
      <SrDarkModal>
        <SrModalContent width='600' onCloseIconClick={onClose}>
          <SrFlex justify='center'>
            <SrText size='2xl' align='center' font='whitney-sc'>
              register partner sale
            </SrText>
          </SrFlex>
          <SrBox marginTop={20}>
            <Formik
              initialValues={createInitialValues({ formFields })}
              validate={createFormValidationFn(formFields)}
              onSubmit={this._onSubmitForm}
              render={(formikBag) => {
                return (
                  <Form>
                    {formFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADMIN}
                      />
                    ))}
                    {adminRegisterPartnerSaleApiState.error && (
                      <SrBox margin='20px 0'>
                        <SrErrorMessage>
                          {adminRegisterPartnerSaleApiState.error.message}
                        </SrErrorMessage>
                      </SrBox>
                    )}
                    <SrBox marginTop={20}>
                      <ButtonDeprecated
                        type='submit'
                        disabled={!formikBag.isValid || adminRegisterPartnerSaleApiState.inProgress}
                      >
                        {adminRegisterPartnerSaleApiState.inProgress
                          ? 'please wait...'
                          : 'create'
                        }
                      </ButtonDeprecated>
                    </SrBox>
                  </Form>
                );
              }}
            />
          </SrBox>
        </SrModalContent>
      </SrDarkModal>
    );
  }
}

SrCreatePartnerSaleModal.displayName = 'SrCreatePartnerSaleModal';

export default SrCreatePartnerSaleModal;
